import { useState, useCallback, useMemo } from 'react';

/**
 * Custom hook to manage image loading state
 * Centralizes image loading logic and provides cached callbacks
 * to prevent unnecessary re-renders
 */
export const useImageLoading = () => {
  // Track which images have been loaded
  const [loadedImages, setLoadedImages] = useState<Record<string, boolean>>({});
  
  // Memoized callback to mark an image as loaded
  const handleImageLoaded = useCallback((imageId: string) => {
    setLoadedImages(prev => ({
      ...prev,
      [imageId]: true
    }));
  }, []);
  
  // Memoized function to check if an image is loaded
  const isImageLoaded = useCallback((imageId: string) => {
    return !!loadedImages[imageId];
  }, [loadedImages]);
  
  // Memoized function to reset loading state
  const resetLoadingState = useCallback(() => {
    setLoadedImages({});
  }, []);
  
  // Memoized value containing all loaded images
  const loadedImagesValue = useMemo(() => loadedImages, [loadedImages]);
  
  return {
    loadedImages: loadedImagesValue,
    handleImageLoaded,
    isImageLoaded,
    resetLoadingState
  };
};
